import { z } from "zod";

const httpsUrl = z.string().trim().url().refine((value) => value.startsWith("https://"), "Use an HTTPS URL");
const storeUrl = (hostname, label) => httpsUrl.refine((value) => {
  try {
    return new URL(value).hostname === hostname;
  } catch {
    return false;
  }
}, `Use the official ${label} URL`);

export const planSettingsSchema = z.object({
  name: z.string().trim().min(2).max(80),
  description: z.string().trim().max(500),
  priceMinor: z.coerce.number().int().min(0),
  intervalCount: z.coerce.number().int().min(1).max(24),
  intervalUnit: z.enum(["month", "year"]),
  isActive: z.boolean(),
  sortOrder: z.coerce.number().int().min(0).max(10000),
  maxBranches: z.coerce.number().int().min(1).max(10000),
  maxStaff: z.coerce.number().int().min(1).max(100000),
  maxProducts: z.coerce.number().int().min(1).max(10000000),
  version: z.coerce.number().int().min(1),
});

export const platformSettingsSchema = z.object({
  iosLatestVersion: z.string().trim().regex(/^\d+(?:\.\d+){1,3}$/, "Use a version such as 1.2.0"),
  iosLatestBuild: z.coerce.number().int().min(1),
  iosMinimumVersion: z.string().trim().regex(/^\d+(?:\.\d+){1,3}$/, "Use a version such as 1.2.0"),
  iosMinimumBuild: z.coerce.number().int().min(1),
  iosStoreUrl: storeUrl("apps.apple.com", "App Store"),
  iosReleaseNotes: z.string().trim().min(4).max(1000),
  androidLatestVersion: z.string().trim().regex(/^\d+(?:\.\d+){1,3}$/, "Use a version such as 1.2.0"),
  androidLatestBuild: z.coerce.number().int().min(1),
  androidMinimumVersion: z.string().trim().regex(/^\d+(?:\.\d+){1,3}$/, "Use a version such as 1.2.0"),
  androidMinimumBuild: z.coerce.number().int().min(1),
  androidStoreUrl: storeUrl("play.google.com", "Play Store"),
  androidReleaseNotes: z.string().trim().min(4).max(1000),
  maintenanceEnabled: z.boolean(),
  maintenanceScope: z.enum(["all", "web", "mobile"]),
  maintenanceMode: z.enum(["read_only", "unavailable"]),
  maintenanceTitle: z.string().trim().min(3).max(120),
  maintenanceMessage: z.string().trim().min(4).max(1000),
  maintenanceStartsAt: z.string().datetime().nullable().optional(),
  maintenanceEndsAt: z.string().datetime().nullable().optional(),
  supportEmail: z.string().trim().email(),
  supportPhone: z.string().trim().max(40),
  termsUrl: httpsUrl,
  privacyUrl: httpsUrl,
  statusUrl: httpsUrl,
  version: z.coerce.number().int().min(1),
}).refine(
  (value) => !value.maintenanceStartsAt || !value.maintenanceEndsAt ||
    new Date(value.maintenanceEndsAt) > new Date(value.maintenanceStartsAt),
  { message: "Maintenance end must be after its start", path: ["maintenanceEndsAt"] },
).refine(
  (value) => value.iosMinimumBuild <= value.iosLatestBuild,
  { message: "iOS minimum build cannot exceed the latest build", path: ["iosMinimumBuild"] },
).refine(
  (value) => value.androidMinimumBuild <= value.androidLatestBuild,
  { message: "Android minimum build cannot exceed the latest build", path: ["androidMinimumBuild"] },
);

export const territorySettingsSchema = z.array(z.object({
  code: z.string().trim().regex(/^[a-z0-9][a-z0-9-]{1,49}$/),
  name: z.string().trim().min(2).max(100),
  region: z.string().trim().max(100),
  dailyTarget: z.coerce.number().int().min(0).max(100000),
  isActive: z.boolean(),
})).max(200);
